import {
  CONVERSIONS,
  CREW_TASKS,
  CREW_TASK_NAMES,
  formatBpAsPercent,
  formatKg,
  isYieldAlarming,
  perBatchG,
  yieldBp,
} from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { NumberPad } from '../components/NumberPad.js';
import { db } from '../sync/db.js';
import type { ReportDraft } from './draft.js';

/**
 * Шаг «цифры». Форма зависит от участка.
 *
 * Везде один принцип: одно число на экране, крупно, с быстрым прибавлением.
 * Оператор переключается между строками плитками, а не листает мелкие поля.
 */

export interface NumbersStepProps {
  draft: ReportDraft;
  siteCode: string;
  onChange(patch: Partial<ReportDraft>): void;
}

function Tabs({
  items,
  active,
  onSelect,
}: {
  items: Array<{ id: string; label: string; value: string }>;
  active: string;
  onSelect(id: string): void;
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-col gap-touch">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className={`flex min-h-touch items-center justify-between rounded-xl px-4 py-3 text-left text-lg font-semibold ${
            item.id === active ? 'bg-sky-700 text-white' : 'bg-muted'
          }`}
        >
          <span>{item.label}</span>
          <span className="text-counter tabular-nums">{item.value}</span>
        </button>
      ))}
    </div>
  );
}

function PressingNumbers({ draft, onChange }: NumbersStepProps): JSX.Element {
  const sizes = useLiveQuery(() => db.productSizes.orderBy('sortOrder').toArray(), [], []);
  const [active, setActive] = useState<string | null>(null);
  /** Годная плитка и брак вводятся по очереди: числа разные, путать их нельзя. */
  const [counting, setCounting] = useState<'good' | 'defect'>('good');
  const current = active ?? sizes[0]?.id ?? null;

  if (sizes.length === 0) return <p className="text-base text-dim">Нет справочника плит</p>;

  const values = counting === 'good' ? draft.outputs : draft.defects;

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setCounting('good')}
          className={`flex-1 rounded-xl px-4 py-3 text-lg font-semibold ${
            counting === 'good' ? 'bg-emerald-700 text-white' : 'bg-muted'
          }`}
        >
          Годная
        </button>
        <button
          type="button"
          onClick={() => setCounting('defect')}
          className={`flex-1 rounded-xl px-4 py-3 text-lg font-semibold ${
            counting === 'defect' ? 'bg-amber-700 text-white' : 'bg-muted'
          }`}
        >
          Брак
        </button>
      </div>

      <Tabs
        items={sizes.map((size) => ({
          id: size.id,
          label: size.name,
          value: String(values[size.id] ?? 0),
        }))}
        active={current ?? ''}
        onSelect={setActive}
      />

      {current && (
        <NumberPad
          key={`${counting}-${current}`}
          value={values[current] ?? 0}
          unit="шт"
          onChange={(value) =>
            onChange(
              counting === 'good'
                ? { outputs: { ...draft.outputs, [current]: value } }
                : { defects: { ...draft.defects, [current]: value } },
            )
          }
        />
      )}

      {counting === 'defect' && (
        <p className="mt-3 text-base text-dim">
          Брак на склад не идёт: его переплавляют тут же, на линии. Со склада списывается
          только то, что теряется при переплавке.
        </p>
      )}
    </div>
  );
}

/**
 * Резка и дробление: помимо агрегата бригада палетирует, грузит и убирает.
 *
 * Палеты считаются двумя числами: запалетить и отгрузить — разная работа.
 * Со склада эти числа ничего не списывают: отгрузку оформляет отдельный
 * документ с покупателем, иначе продукция ушла бы дважды.
 */
function CrewWorkBlock({ draft, onChange }: NumbersStepProps): JSX.Element {
  const crew = draft.crewWork ?? { palletsPacked: 0, palletsLoaded: 0, tasks: [] };
  const [active, setActive] = useState<'packed' | 'loaded'>('packed');

  const patch = (change: Partial<typeof crew>): void => {
    onChange({ crewWork: { ...crew, ...change } });
  };

  return (
    <section className="mt-6 border-t border-line pt-6">
      <h3 className="mb-1 text-lg font-semibold">Работа бригады</h3>
      <p className="mb-4 text-base text-dim">
        Палеты и прочие дела по цеху. На склад это не влияет — учитывается как работа.
      </p>

      <Tabs
        items={[
          { id: 'packed', label: 'Запалечено палет', value: String(crew.palletsPacked) },
          { id: 'loaded', label: 'Отгружено палет', value: String(crew.palletsLoaded) },
        ]}
        active={active}
        onSelect={(id) => setActive(id as 'packed' | 'loaded')}
      />

      <NumberPad
        key={active}
        value={active === 'packed' ? crew.palletsPacked : crew.palletsLoaded}
        unit="шт"
        onChange={(value) =>
          patch(active === 'packed' ? { palletsPacked: value } : { palletsLoaded: value })
        }
      />

      <p className="mb-2 mt-5 text-base text-dim">Что ещё делали за смену:</p>
      <div className="flex flex-col gap-2">
        {CREW_TASKS.map((task) => (
          <label
            key={task}
            className="flex items-center gap-3 rounded-xl bg-muted px-4 py-3 text-base"
          >
            <input
              type="checkbox"
              checked={crew.tasks.includes(task)}
              onChange={() =>
                patch({
                  tasks: crew.tasks.includes(task)
                    ? crew.tasks.filter((item) => item !== task)
                    : [...crew.tasks, task],
                })
              }
              className="h-5 w-5"
            />
            {CREW_TASK_NAMES[task]}
          </label>
        ))}
      </div>
    </section>
  );
}

/**
 * Передел: переработали одно — получили другое.
 *
 * Что во что превращается, зависит от агрегата, а не от участка: на «Резке
 * и дроблении» гильотина делает резаную плёнку, а дробилка рядом — дроблёнку,
 * и норма выхода у них разная. Раньше это определялось участком, и с новым
 * техпроцессом такая привязка давала бы неверные цифры.
 */
function ProcessingNumbers({ draft, onChange }: NumbersStepProps): JSX.Element {
  const machine = useLiveQuery(() => db.machines.get(draft.machineId), [draft.machineId]);
  const materials = useLiveQuery(() => db.materialTypes.toArray(), [], []);
  const norms = useLiveQuery(
    () => db.machineNorms.where('machineId').equals(draft.machineId).toArray(),
    [draft.machineId],
    [],
  );
  const [active, setActive] = useState<'produced' | 'film'>('produced');

  const conversion = machine
    ? CONVERSIONS.find((item) => item.machineCode === machine.code)
    : undefined;
  if (!conversion) {
    return <p className="text-base text-dim">Для этого агрегата передел не описан</p>;
  }

  const produced = materials.find((material) => material.code === conversion.to);
  const film = materials.find((material) => material.code === conversion.from);
  if (!produced || !film) return <p className="text-base text-dim">Нет справочника сырья</p>;

  const producedG = draft.materials[produced.id]?.qtyG ?? 0;
  const filmG = draft.materials[film.id]?.qtyG ?? 0;

  const normBp = norms.find((norm) => norm.key === 'YIELD_NORM_BP')?.valueInt ?? null;
  const actualBp = yieldBp(producedG, filmG);

  const setMaterial = (id: string, direction: 'PRODUCED' | 'CONSUMED', qtyG: number): void => {
    onChange({ materials: { ...draft.materials, [id]: { direction, qtyG } } });
  };

  return (
    <div>
      <Tabs
        items={[
          { id: 'produced', label: `Произведено: ${produced.name}`, value: formatKg(producedG) },
          { id: 'film', label: 'Переработано плёнки', value: formatKg(filmG) },
        ]}
        active={active}
        onSelect={(id) => setActive(id as 'produced' | 'film')}
      />

      {actualBp !== null && (
        <p
          className={`mb-4 rounded-xl px-4 py-3 text-base ${
            normBp !== null && isYieldAlarming(actualBp, normBp)
              ? 'bg-amber-500 text-black'
              : 'bg-muted text-body'
          }`}
        >
          Выход {formatBpAsPercent(actualBp)}
          {normBp !== null && ` при норме ${formatBpAsPercent(normBp)}`}
        </p>
      )}

      {active === 'produced' ? (
        <NumberPad
          key="produced"
          value={producedG / 100}
          unit="кг"
          decimals={1}
          quickAdd={[1, 5, 10, 50]}
          onChange={(value) => setMaterial(produced.id, 'PRODUCED', value * 100)}
        />
      ) : (
        <div>
          <p className="mb-2 text-base text-dim">
            Поле необязательное, но по нему считается процент выхода
          </p>
          <NumberPad
            key="film"
            value={filmG / 100}
            unit="кг"
            decimals={1}
            quickAdd={[1, 5, 10, 50]}
            onChange={(value) => setMaterial(film.id, 'CONSUMED', value * 100)}
          />
        </div>
      )}
    </div>
  );
}

function MixingNumbers({ draft, onChange }: NumbersStepProps): JSX.Element {
  const materials = useLiveQuery(() => db.materialTypes.orderBy('sortOrder').toArray(), [], []);
  const recipe = useLiveQuery(() => db.recipes.toArray(), [], []);
  const [active, setActive] = useState<string>('batches');

  const mix = materials.find((material) => material.code === 'MIX');
  const inputs = materials.filter((material) =>
    ['AGGLOMERATE', 'CAPSULATE', 'CRUSHED', 'COLORANT', 'SAND_DRY'].includes(material.code),
  );
  if (!mix) return <p className="text-base text-dim">Нет справочника сырья</p>;

  const activeRecipe = recipe.find((item) => item.isActive);
  const batches = draft.batchesCount ?? 0;

  const setMaterial = (id: string, direction: 'PRODUCED' | 'CONSUMED', qtyG: number): void => {
    onChange({ materials: { ...draft.materials, [id]: { direction, qtyG } } });
  };

  const items = [
    { id: 'batches', label: 'Количество замесов', value: String(batches) },
    {
      id: mix.id,
      label: 'Произведено замеса',
      value: formatKg(draft.materials[mix.id]?.qtyG ?? 0),
    },
    ...inputs.map((material) => ({
      id: material.id,
      label: `Расход: ${material.name}`,
      value: formatKg(draft.materials[material.id]?.qtyG ?? 0),
    })),
  ];

  const perBatch = (materialId: string): string | null => {
    const total = draft.materials[materialId]?.qtyG ?? 0;
    const value = perBatchG(total, batches);
    return value === null ? null : formatKg(value);
  };

  const deviationNote = (materialId: string): string | null => {
    if (!activeRecipe || batches <= 0) return null;
    const item = activeRecipe.items.find((entry) => entry.materialTypeId === materialId);
    if (!item) return null;
    const total = draft.materials[materialId]?.qtyG ?? 0;
    const value = perBatchG(total, batches);
    if (value === null) return null;
    if (value < item.qtyPerBatchMinG) return 'меньше рецептуры';
    if (value > item.qtyPerBatchMaxG) return 'больше рецептуры';
    return null;
  };

  return (
    <div>
      <Tabs items={items} active={active} onSelect={setActive} />

      {active === 'batches' ? (
        <NumberPad
          key="batches"
          value={batches}
          unit="замесов"
          quickAdd={[1, 2, 5, 10]}
          onChange={(value) => onChange({ batchesCount: value })}
        />
      ) : (
        <div>
          {perBatch(active) && (
            <p
              className={`mb-3 rounded-xl px-4 py-3 text-base ${
                deviationNote(active) ? 'bg-amber-500 text-black' : 'bg-muted text-body'
              }`}
            >
              На один замес: {perBatch(active)} кг
              {deviationNote(active) && ` — ${deviationNote(active)}`}
            </p>
          )}
          <NumberPad
            key={active}
            value={(draft.materials[active]?.qtyG ?? 0) / 100}
            unit="кг"
            decimals={1}
            quickAdd={[1, 5, 10, 50]}
            onChange={(value) =>
              setMaterial(active, active === mix.id ? 'PRODUCED' : 'CONSUMED', value * 100)
            }
          />
        </div>
      )}
    </div>
  );
}

export function NumbersStep(props: NumbersStepProps): JSX.Element {
  if (props.siteCode === 'PRESSING') return <PressingNumbers {...props} />;
  if (props.siteCode === 'MIXING') return <MixingNumbers {...props} />;

  // Резка и дробление: к переделу добавляется работа бригады по цеху
  if (props.siteCode === 'CRUSHING') {
    return (
      <div>
        <ProcessingNumbers {...props} />
        <CrewWorkBlock {...props} />
      </div>
    );
  }

  return <ProcessingNumbers {...props} />;
}
